import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Button, Spinner } from '@indeed/ifl-components';
import { QuickApply, DocReview } from '@indeed/ifl-icons';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useApolloClient } from '@apollo/client';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { AutoApplyHeader } from '../../../components/AutoApplyHeader/AutoApplyHeader';
import { ErrorDialog } from '../../../components/ErrorDialog/ErrorDialog';
import { OptionCard, OptionCardGroup } from '../../../components/OptionCard';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import { jsProThemeButton } from '../components/jsProTheme/jsProTheme';
import GettextFactory from '../../../util/GettextFactory';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { useLocation } from 'react-router-dom';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';
import {
    useGetAutoApplyModeQuery,
    useUpdateAutoApplyModeMutation,
    GetAutoApplyModeDocument,
} from './__generated__/AutomationLevelPage.generated';
import type { AutoApplyMode } from '../../../__generated__/graphql-base-types';

const AutomationLevelPage: React.FC = () => {
    const gt = GettextFactory();
    const { goForward, exitSubFlow, loading: pageFlowLoading } = usePageFlow();
    const apolloClient = useApolloClient();
    const eventLogger = useEventLogger();
    const location = useLocation();
    const { getGroup } = useProctorGroups();
    const isApplyForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;
    const copy = useApplyForMeCopy();

    const isEditMode = location.pathname.endsWith('/edit');
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    const {
        data,
        loading: queryLoading,
        error: queryError,
        refetch,
    } = useGetAutoApplyModeQuery({
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });

    const [updateAutoApplyMode, { loading: updateLoading }] = useUpdateAutoApplyModeMutation();

    useEffect(() => {
        if (queryError) {
            setShowErrorDialog(true);
        }
    }, [queryError]);

    // A stored Apply For Me mode is not selectable in the disabled treatment. Leave the
    // form unselected until the user explicitly chooses Review Before Submit.
    const selectedMode =
        isApplyForMeDisabled && data?.autoApplyInfo?.mode === 'APPLY_FOR_ME'
            ? null
            : (data?.autoApplyInfo?.mode ?? null);

    const updateModeCache = (mode: AutoApplyMode | null) => {
        apolloClient.cache.writeQuery({
            query: GetAutoApplyModeDocument,
            data: {
                autoApplyInfo: {
                    __typename: 'AutoApplyInfoPayload',
                    mode,
                },
            },
        });
    };

    const handleSelect = (mode: AutoApplyMode) => {
        updateModeCache(mode);
        setShowErrorDialog(false);
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: `automation-level-${mode}`,
        });
    };

    const handleContinue = async () => {
        if (!selectedMode) return;

        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: 'continue',
        });

        try {
            const result = await updateAutoApplyMode({
                variables: {
                    input: { mode: selectedMode },
                },
            });

            // ITV check: verify server response matches user intent
            const responseMode = result.data?.updateAutoApply?.mode;
            if (responseMode !== selectedMode) {
                console.error(`ITV mismatch: requested ${selectedMode}, got ${responseMode}`);
                setShowErrorDialog(true);
                return;
            }

            // In edit mode this page is the final sub-flow step, so exit the
            // detour explicitly back to the origin (Review). In normal mode
            // advance to the next main-flow step (IAFE-383).
            if (isEditMode) {
                await exitSubFlow('/auto-apply-setup/review');
            } else {
                await goForward();
            }
        } catch (error) {
            console.error('Failed to update automation mode:', error);
            setShowErrorDialog(true);
        }
    };

    const helmet = (
        <Helmet>
            <title>{copy.automationLevelTitle}</title>
        </Helmet>
    );

    const errorDialog = (
        <ErrorDialog
            isOpen={showErrorDialog}
            onOpenChange={setShowErrorDialog}
            onAction={
                queryError
                    ? async () => {
                          await refetch();
                      }
                    : handleContinue
            }
        />
    );

    if (queryLoading || queryError || !data) {
        return (
            <PageTransition pageKey="automation-level">
                {helmet}
                <Box sx={{ padding: 4, textAlign: 'center' }}>
                    <Spinner title={gt.gettext('Loading')} />
                </Box>
                {errorDialog}
            </PageTransition>
        );
    }

    return (
        <PageTransition pageKey="automation-level">
            {helmet}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    gap: 5,
                }}
            >
                <AutoApplyHeader title={gt.gettext('Choose level of automation')} />

                <OptionCardGroup>
                    {!isApplyForMeDisabled && (
                        <OptionCard
                            name="automation-level"
                            value="APPLY_FOR_ME"
                            checked={selectedMode === 'APPLY_FOR_ME'}
                            onChange={() => handleSelect('APPLY_FOR_ME')}
                            icon={<QuickApply size="md" aria-hidden />}
                            title={copy.applyForMeModeTitle}
                            description={gt.gettext(
                                "We'll prepare and submit applications to jobs that match your preferences.",
                            )}
                        />
                    )}
                    <OptionCard
                        name="automation-level"
                        value="REVIEW_BEFORE_SUBMIT"
                        checked={selectedMode === 'REVIEW_BEFORE_SUBMIT'}
                        onChange={() => handleSelect('REVIEW_BEFORE_SUBMIT')}
                        icon={<DocReview size="md" aria-hidden />}
                        title={gt.gettext('Review before submit')}
                        description={gt.gettext(
                            "We'll prepare applications to relevant jobs for you to review and submit.",
                        )}
                    />
                </OptionCardGroup>

                <Button
                    onClick={handleContinue}
                    disabled={!selectedMode || pageFlowLoading || updateLoading}
                    loading={pageFlowLoading || updateLoading}
                    sx={jsProThemeButton.primary}
                    full
                >
                    {isEditMode ? gt.gettext('Update') : gt.gettext('Continue')}
                </Button>
            </Box>

            {errorDialog}
        </PageTransition>
    );
};

export default AutomationLevelPage;
